/**
 * Owner row action menu — the three-dot dropdown for the Owners list.
 *
 * Each item names the record it opens. "Edit owner" used to open the ACCOUNT
 * form — a dialog headed "Edit user", for an item labelled owner — while a
 * second item, "Edit owner details", opened the owner record the first one's
 * name promised. The two are separate records (see @/lib/owner-directory), so
 * they get one item each: "Edit owner" for the billing record, "Edit login &
 * access" for the portal account, and neither is offered when that half of the
 * record doesn't exist.
 *
 * Record actions (open profile, edit either half, add the missing half, delete)
 * are delegated to the page via callbacks. Account actions (set/reset password,
 * act as owner, activate/deactivate) are implemented here against the same edge
 * functions the Settings user-roles panel uses, so behaviour matches.
 */
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  MoreHorizontal,
  Eye,
  Pencil,
  Building2,
  Mail,
  KeyRound,
  UserCog,
  MailQuestion,
  EyeOff,
  Trash2,
  Loader2,
  Presentation,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  setUserPassword,
  setUserActive,
  sendPasswordResetLink,
} from "@/lib/admin-account-actions";
import { setDemoAccountFlag } from "@/lib/set-demo-account-flag";
import { getSiteUrlForPath } from "@/lib/site-url";
import type { AdminProfileDirectoryRow } from "@/lib/fetch-admin-profiles-directory";
import type { OwnerListRow } from "@/hooks/v2/useOwnerDetail";
import { invalidateOwnerCaches } from "@/hooks/v2/owner-detail-keys";

/**
 * The identity the menu labels itself with.
 *
 * Only a name, an entity type and an email are ever read (plus the lessor id,
 * used only to narrow cache invalidation below), so an owner who has a portal
 * account but no billing entity yet can still open this menu — they just won't
 * be offered the entity-only actions below.
 */
export type OwnerActionsSubject = {
  name: string;
  type?: OwnerListRow["type"] | null;
  email?: string | null;
  /** The lessor record's id, when there is one — absent for an account-only owner. */
  id?: string | null;
};

export function OwnerActionsMenu({
  owner,
  account,
  isSuperAdmin,
  hasEntity = true,
  onOpenProfile,
  onEditOwner,
  onEditAccount,
  onAddBillingEntity,
  onSendLoginLink,
  onDelete,
}: {
  owner: OwnerActionsSubject;
  account: AdminProfileDirectoryRow | null;
  isSuperAdmin: boolean;
  /**
   * Whether a `lessors` row backs this owner. "Edit owner" and "Delete owner"
   * both act on that record, so with no entity there is nothing for them to
   * open or remove — offering them would only produce a dead click.
   */
  hasEntity?: boolean;
  /**
   * Omitted in the owner profile's own header (COL-475), along with
   * `onEditOwner`: one item would open the page you are on, the other a second
   * editor beside the profile's inline Edit button. Each item hides without it.
   */
  onOpenProfile?: () => void;
  /** Opens the OWNER record form (name, type, representor, bank). */
  onEditOwner?: () => void;
  /** Opens the PORTAL ACCOUNT form (role, property assignments, contracts). */
  onEditAccount?: () => void;
  /** Offered in place of "Edit owner" when there is no billing record to edit. */
  onAddBillingEntity?: () => void;
  onSendLoginLink?: () => void;
  onDelete: () => void;
}) {
  const { t } = useLanguage();
  const qc = useQueryClient();
  // "Act as owner" is open to admins as well as super admins (the edge
  // function accepts both), so it reads its own flag rather than riding on the
  // isSuperAdmin prop that gates the destructive items below. `canManageTenant`
  // is the client mirror of the SQL `has_role(uid, 'admin')` the demo-flag
  // trigger enforces — same predicate, so the item can't appear and then error.
  const { canActAs, actAsOwner, canManageTenant } = useAuth();
  const [pwOpen, setPwOpen] = useState(false);
  const [pwValue, setPwValue] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [toggleOpen, setToggleOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [toggleBusy, setToggleBusy] = useState(false);
  const [demoBusy, setDemoBusy] = useState(false);

  const hasAccount = !!account;
  const userId = account?.id ?? null;
  const isActive = account?.is_active ?? true;
  const isDemoAccount = account?.is_demo_account ?? false;

  // The record group (open / edit / add the missing half) can come out empty
  // in the profile header, so a separator only renders after a group that did.
  const showEditOwner = hasEntity && !!onEditOwner;
  const showAddBillingEntity = !hasEntity && !!onAddBillingEntity;
  const showEditAccount = !!onEditAccount && hasAccount;
  const hasRecordItems =
    !!onOpenProfile || showEditOwner || showAddBillingEntity || showEditAccount;
  const showSendLoginLink = !hasAccount && !!onSendLoginLink && !!owner.email;

  const refetch = () => {
    qc.invalidateQueries({ queryKey: ["admin-role-mgmt-users"] });
    invalidateOwnerCaches(qc, owner?.id ?? null);
  };

  /**
   * Email the owner a password-reset link — they choose the new password.
   *
   * Goes through the admin helper rather than AuthContext.resetPasswordForEmail:
   * that one toasts "check your inbox" itself, which both double-toasts here and
   * addresses the wrong person (the admin isn't the recipient).
   */
  async function confirmResetPassword() {
    const email = account?.email;
    if (!email) return;
    setResetBusy(true);
    try {
      const res = await sendPasswordResetLink({
        email,
        redirectTo: getSiteUrlForPath("/reset-password"),
      });
      if (!res.ok) {
        toast.error(
          res.error ??
            t(
              "admin.owners.actions.resetLinkFailed",
              "Could not send the reset link"
            )
        );
        return;
      }
      // Never "sent" flat out: the function reports success for an address with
      // no login account too (anti-enumeration), which is exactly the case for
      // an owner who has a profile row but never got an auth account.
      toast.success(
        `${t("admin.owners.actions.resetRequestedFor", "Password reset requested for")} ${email}`,
        {
          description: t(
            "admin.owners.actions.resetRequestedDescription",
            "A link is only emailed if that address has a login account — nothing is sent otherwise."
          ),
        }
      );
      setResetOpen(false);
    } finally {
      setResetBusy(false);
    }
  }

  async function submitPassword() {
    if (!userId || pwValue.trim().length < 8) {
      toast.error(
        t(
          "admin.owners.actions.passwordTooShort",
          "Password must be at least 8 characters."
        )
      );
      return;
    }
    setPwBusy(true);
    try {
      const res = await setUserPassword(userId, pwValue.trim());
      if (!res.ok) {
        toast.error(
          res.error ??
            t(
              "admin.owners.actions.passwordUpdateFailed",
              "Could not update password"
            )
        );
        return;
      }
      toast.success(
        res.message ??
          t("admin.owners.actions.passwordUpdated", "Password updated")
      );
      setPwOpen(false);
      setPwValue("");
    } finally {
      setPwBusy(false);
    }
  }

  /**
   * Flip the sales-demo flag on this account (COL-447).
   *
   * Presentation-only: it lets this one account reach the tenant dashboard as
   * well as the owner portal without signing out. It grants no reads — the
   * account still sees exactly what its own `property_owners` rows and booking
   * allow. There is no confirm step because there is nothing to undo: the same
   * item turns it straight back off.
   */
  async function toggleDemoAccount() {
    if (!userId) return;
    setDemoBusy(true);
    try {
      const res = await setDemoAccountFlag(userId, !isDemoAccount);
      if (!res.ok) {
        toast.error(
          res.error ??
            t(
              "admin.owners.actions.demoUpdateFailed",
              "Could not update the demo flag"
            )
        );
        return;
      }
      toast.success(
        isDemoAccount
          ? t("admin.owners.actions.demoCleared", "No longer a demo account")
          : t("admin.owners.actions.demoSet", "Marked as the demo account"),
        {
          description: isDemoAccount
            ? t(
                "admin.owners.actions.demoClearedDescription",
                "They see the owner portal only, like every other owner."
              )
            : t(
                "admin.owners.actions.demoSetDescription",
                "They can switch to the tenant dashboard without signing out — once they also hold a booking."
              ),
        }
      );
      refetch();
    } finally {
      setDemoBusy(false);
    }
  }

  async function confirmToggleActive() {
    if (!userId) return;
    setToggleBusy(true);
    try {
      const res = await setUserActive(userId, !isActive);
      if (!res.ok) {
        toast.error(
          res.error ??
            t(
              "admin.owners.actions.accountUpdateFailed",
              "Could not update account"
            )
        );
        return;
      }
      toast.success(
        isActive
          ? t("admin.owners.actions.accountDeactivated", "Account deactivated")
          : t("admin.owners.actions.accountActivated", "Account activated")
      );
      setToggleOpen(false);
      refetch();
    } finally {
      setToggleBusy(false);
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground data-[state=open]:bg-muted data-[state=open]:text-foreground"
            aria-label={`${t("admin.owners.actions.actionsFor", "Actions for")} ${owner.name}`}
            onClick={(e) => e.stopPropagation()}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-56 rounded-xl"
          onClick={(e) => e.stopPropagation()}
        >
          {onOpenProfile && (
            <DropdownMenuItem className="gap-2" onClick={onOpenProfile}>
              <Eye className="h-4 w-4 text-muted-foreground" />
              {t("admin.owners.actions.openProfile", "Open profile")}
            </DropdownMenuItem>
          )}
          {showEditOwner && (
            <DropdownMenuItem className="gap-2" onClick={onEditOwner}>
              <Pencil className="h-4 w-4 text-muted-foreground" />
              {owner.type === "Company"
                ? t("admin.owners.actions.editCompany", "Edit company")
                : t("admin.owners.actions.editOwner", "Edit owner")}
            </DropdownMenuItem>
          )}
          {showAddBillingEntity && (
            <DropdownMenuItem className="gap-2" onClick={onAddBillingEntity}>
              <Building2 className="h-4 w-4 text-muted-foreground" />
              {t("admin.owners.actions.addBillingEntity", "Add billing entity")}
            </DropdownMenuItem>
          )}
          {showEditAccount && (
            <DropdownMenuItem className="gap-2" onClick={onEditAccount}>
              <UserCog className="h-4 w-4 text-muted-foreground" />
              {t("admin.owners.actions.editLoginAccess", "Edit login & access")}
            </DropdownMenuItem>
          )}

          {hasAccount ? (
            <>
              {hasRecordItems && <DropdownMenuSeparator />}
              {/* Onboarding invitations live on the APARTMENT rows (owner modal →
                  Apartments), not here: a token is issued per apartment, so an
                  owner-level "send" can't say which apartment it is for. */}
              {/* "Set password" is the admin override — sets one directly, so the
                  owner can sign in immediately. */}
              <DropdownMenuItem
                className="gap-2"
                onClick={() => {
                  setPwValue("");
                  setPwOpen(true);
                }}
              >
                <KeyRound className="h-4 w-4 text-muted-foreground" />
                {t("admin.owners.actions.setPassword", "Set password")}
              </DropdownMenuItem>
              {/* "Reset password" hands control to the owner — emails them a
                  reset link instead of an admin choosing their password. */}
              <DropdownMenuItem
                className="gap-2"
                onClick={() => setResetOpen(true)}
                disabled={resetBusy || !account?.email}
              >
                {resetBusy ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <MailQuestion className="h-4 w-4 text-muted-foreground" />
                )}
                {t("admin.owners.actions.resetPassword", "Reset password")}
              </DropdownMenuItem>
              {canActAs && userId && (
                <DropdownMenuItem
                  className="gap-2"
                  onClick={() => void actAsOwner(userId)}
                >
                  <Eye className="h-4 w-4 text-muted-foreground" />
                  {t("admin.owners.actions.actAsOwner", "Act as owner")}
                </DropdownMenuItem>
              )}
              {/* The sales-demo account. Admin-tier only, matching the
                  `enforce_profile_is_demo_role` trigger — a tech account would
                  see the item and get a raw database error. Kept next to the
                  account actions because that is what it changes: which
                  screens this login is offered, nothing about its data. */}
              {canManageTenant && userId && (
                <DropdownMenuItem
                  className="gap-2"
                  disabled={demoBusy}
                  onClick={() => void toggleDemoAccount()}
                >
                  {demoBusy ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Presentation className="h-4 w-4 text-muted-foreground" />
                  )}
                  {isDemoAccount
                    ? t(
                        "admin.owners.actions.unmarkDemoAccount",
                        "Remove demo account"
                      )
                    : t(
                        "admin.owners.actions.markDemoAccount",
                        "Mark as demo account"
                      )}
                </DropdownMenuItem>
              )}
              {isSuperAdmin && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="gap-2 text-amber-600 focus:bg-amber-50 focus:text-amber-700"
                    onClick={() => setToggleOpen(true)}
                  >
                    <EyeOff className="h-4 w-4" />
                    {isActive
                      ? t(
                          "admin.owners.actions.deactivateAccount",
                          "Deactivate account"
                        )
                      : t(
                          "admin.owners.actions.activateAccount",
                          "Activate account"
                        )}
                  </DropdownMenuItem>
                </>
              )}
            </>
          ) : (
            showSendLoginLink && (
              <>
                {hasRecordItems && <DropdownMenuSeparator />}
                <DropdownMenuItem className="gap-2" onClick={onSendLoginLink}>
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  {t("admin.owners.actions.sendLoginLink", "Send login link")}
                </DropdownMenuItem>
              </>
            )
          )}

          {hasEntity && (
            <>
              {(hasRecordItems || hasAccount || showSendLoginLink) && (
                <DropdownMenuSeparator />
              )}
              <DropdownMenuItem
                className="gap-2 text-rose-600 focus:bg-rose-50 focus:text-rose-700"
                onClick={onDelete}
              >
                <Trash2 className="h-4 w-4" />
                {t("admin.owners.actions.deleteOwner", "Delete owner")}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Set password (admin override) */}
      <Dialog open={pwOpen} onOpenChange={(o) => !o && setPwOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t(
                "admin.owners.actions.setPasswordDialog.title",
                "Set password"
              )}
            </DialogTitle>
            <DialogDescription>
              {t(
                "admin.owners.actions.setPasswordDialog.descriptionPrefix",
                "Set a new password for"
              )}{" "}
              <span className="font-medium text-foreground">
                {account?.email}
              </span>
              .{" "}
              {t(
                "admin.owners.actions.setPasswordDialog.descriptionSuffix",
                "They can sign in with it immediately."
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="owner-new-pw" className="text-xs font-medium">
              {t(
                "admin.owners.actions.setPasswordDialog.label",
                "New password"
              )}
            </Label>
            <Input
              id="owner-new-pw"
              type="text"
              autoComplete="off"
              placeholder={t(
                "admin.owners.actions.setPasswordDialog.placeholder",
                "At least 8 characters"
              )}
              value={pwValue}
              onChange={(e) => setPwValue(e.target.value)}
              disabled={pwBusy}
            />
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setPwOpen(false)}
              disabled={pwBusy}
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              onClick={submitPassword}
              disabled={pwBusy || pwValue.trim().length < 8}
            >
              {pwBusy
                ? t("admin.owners.actions.saving", "Saving…")
                : t("admin.owners.actions.savePassword", "Save password")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activate / deactivate confirm */}
      <AlertDialog open={toggleOpen} onOpenChange={setToggleOpen}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isActive
                ? t(
                    "admin.owners.actions.deactivateConfirmTitle",
                    "Deactivate account?"
                  )
                : t(
                    "admin.owners.actions.activateConfirmTitle",
                    "Activate account?"
                  )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isActive
                ? `${owner.name} ${t("admin.owners.actions.deactivateConfirmBody", "will lose access to the owner portal until reactivated.")}`
                : `${owner.name} ${t("admin.owners.actions.activateConfirmBody", "will regain access to the owner portal.")}`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={toggleBusy}>
              {t("common.cancel", "Cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void confirmToggleActive();
              }}
              disabled={toggleBusy}
              className={
                isActive
                  ? "bg-amber-600 text-white hover:bg-amber-600/90"
                  : undefined
              }
            >
              {toggleBusy
                ? t("admin.owners.actions.working", "Working…")
                : isActive
                  ? t("admin.owners.actions.deactivate", "Deactivate")
                  : t("admin.owners.actions.activate", "Activate")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm password reset — this emails a real owner, so never on a
          single misclick. */}
      <AlertDialog
        open={resetOpen}
        onOpenChange={(o) => !resetBusy && setResetOpen(o)}
      >
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(
                "admin.owners.actions.resetConfirmTitle",
                "Send a password reset link?"
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {account?.email}{" "}
              {t(
                "admin.owners.actions.resetConfirmBody",
                "will be emailed a link to choose a new password, provided that address has a login account — no email goes out if it doesn't. Their current password keeps working until they use the link."
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resetBusy}>
              {t("common.cancel", "Cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void confirmResetPassword();
              }}
              disabled={resetBusy}
            >
              {resetBusy
                ? t("admin.owners.actions.sending", "Sending…")
                : t("admin.owners.actions.sendResetLink", "Send reset link")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
